/**
 * src/api/admin.ts
 *
 * File-level:
 * - Small admin API client used by the frontend.
 * - Ensures requests are sent to the backend origin (runtime-detectable) to avoid calling the frontend origin.
 *
 * Notes:
 * - Reads backend origin from window.__BACKEND_ORIGIN__ when available (recommended to inject from the server).
 * - Falls back to http://localhost:5000 which matches the dev backend used in this project.
 */

/**
 * AdminLoginPayload
 *
 * @description Payload for admin login request.
 */
export interface AdminLoginPayload {
  userId: string;
  password: string;
}

/**
 * AdminLoginResult
 *
 * @description Expected shape of login responses from the backend.
 */
export interface AdminLoginResult {
  success: boolean;
  message?: string;
  user?: {
    userId?: string;
    displayName?: string;
    [key: string]: any;
  };
  token?: string;
}

import { API_CONFIG } from '../config/api.config';

/**
 * getBackendOrigin
 *
 * @description Determine the backend origin at runtime.
 * Priority:
 *  1. window.__BACKEND_ORIGIN__ (recommended; can be injected by server)
 *  2. Centralized API_CONFIG
 *
 * @returns backend origin string (no trailing slash)
 */
function getBackendOrigin(): string {
  const win = window as any;
  if (win && typeof win.__BACKEND_ORIGIN__ === 'string' && win.__BACKEND_ORIGIN__) {
    return win.__BACKEND_ORIGIN__.replace(/\/+$/, '');
  }

  // Use centralized configuration
  return API_CONFIG.getBackendOrigin().replace(/\/+$/, '');
}

/**
 * loginAdmin
 *
 * @description Send admin login request to the backend.
 * Uses JSON body { userId, password } and returns parsed JSON from server.
 *
 * @param payload AdminLoginPayload
 * @returns Promise<AdminLoginResult>
 */
export async function loginAdmin(payload: AdminLoginPayload): Promise<AdminLoginResult> {
  const base = getBackendOrigin();
  const url = `${base}/api/admin/auth/login`;

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      // include credentials if backend uses cookies/sessions
      credentials: 'include',
      body: JSON.stringify(payload)
    });

    // Try to parse JSON; if not JSON, return a clear error message
    let data: any = null;
    try {
      data = await res.json();
    } catch (err) {
      return {
        success: false,
        message: `Invalid JSON response from server (status ${res.status})`
      };
    }

    if (!res.ok) {
      return {
        success: false,
        message: data?.message || `Request failed with status ${res.status}`
      };
    }

    return data as AdminLoginResult;
  } catch (err: any) {
    return {
      success: false,
      message: String(err?.message ?? err)
    };
  }
}

/**
 * pingAdminLogin
 *
 * @description A small helper to check availability of the admin-login endpoint (returns boolean).
 * Useful for UI debug to display targeted guidance instead of generic 404.
 *
 * @returns Promise<boolean>
 */
export async function pingAdminLogin(): Promise<boolean> {
  const base = getBackendOrigin();
  const url = `${base}/api/admin/auth/login`;
  try {
    const res = await fetch(url, { method: 'OPTIONS' });
    return res.status >= 200 && res.status < 500; // treat any server reachable response as available
  } catch {
    return false;
  }
}

// ============================================
// Admin Dashboard API Functions
// ============================================

// Use API_CONFIG for consistent backend origin resolution
const API_URL = API_CONFIG.getBackendOrigin();
const DEFAULT_TIMEOUT = 8000;

/**
 * Stats interface
 */
export interface Stats {
  totalProfiles: number;
  newProfiles: number;
  paidProfiles: number;
  notPaidProfiles?: number;
  forgotPasswordCount: number;
  profileChanges?: number;
  updatePhotos?: number;
  updateDocuments?: number;
  updateVideos?: number;
}

/**
 * fetchJson - Helper for JSON API calls with timeout
 */
async function fetchJson<T>(url: string, options: RequestInit = {}): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT);
  
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      throw new Error(`Request to ${url} failed with status ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
}

/**
 * fetchAdminStats - Get dashboard statistics
 */
export async function fetchAdminStats(): Promise<Stats> {
  const data = await fetchJson<Stats>(`${API_URL}/api/admin/stats`);
  return data;
}

/**
 * fetchModerationItems - Get moderation queue items
 */
export async function fetchModerationItems(): Promise<any[]> {
  try {
    const data = await fetchJson<any>(`${API_URL}/api/admin/moderation`);
    return Array.isArray(data) ? data : (data.items || data.list || []);
  } catch (error) {
    console.warn('[fetchModerationItems] Error:', error);
    return [];
  }
}

/**
 * fetchForgotPasswordRequests - Get forgot password requests
 */
export async function fetchForgotPasswordRequests(): Promise<any[]> {
  try {
    const data = await fetchJson<any>(`${API_URL}/api/admin/forgot-requests`);
    return Array.isArray(data) ? data : (data.requests || data.list || []);
  } catch (error) {
    console.warn('[fetchForgotPasswordRequests] Error:', error);
    return [];
  }
}

/**
 * fetchHomeContent - Get home page content
 */
export async function fetchHomeContent(): Promise<any> {
  try {
    const data = await fetchJson<any>(`${API_URL}/api/admin/home-content`);
    return data || { title: '', subtitle: '', ctaText: '' };
  } catch (error) {
    console.warn('[fetchHomeContent] Error:', error);
    return { title: '', subtitle: '', ctaText: '' };
  }
}

/**
 * updateHomeContent - Update home page content
 */
export async function updateHomeContent(updates: any): Promise<any> {
  try {
    const data = await fetchJson<any>(`${API_URL}/api/admin/home-content`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });
    return data;
  } catch (error) {
    console.error('[updateHomeContent] Error:', error);
    throw error;
  }
}

/**
 * fetchUserDetails - Get user details by ID
 */
export async function fetchUserDetails(userId: string): Promise<any> {
  try {
    const data = await fetchJson<any>(`${API_URL}/api/admin/user/${encodeURIComponent(userId)}`);
    return data ?? null;
  } catch (err) {
    console.warn('[fetchUserDetails] failed, using fallback', err);
    return {
      userId,
      name: 'Sample User',
      email: 'user@example.com'
    };
  }
}

/**
 * suspendUser - Suspend a user account
 */
export async function suspendUser(userId: string): Promise<void> {
  try {
    await fetchJson(`${API_URL}/api/admin/user/${encodeURIComponent(userId)}/suspend`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
  } catch (error) {
    console.error('[suspendUser] Error:', error);
    throw error;
  }
}
